import { zodResolver } from "@hookform/resolvers/zod"
import {
  isPrismicHttpErrorResponse,
  isRequestError,
} from "@prismicio/editor-support/Http"
import {
  AnimatedFormError,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogHeader,
  EditableText,
  Form,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
  useAlert,
  useToast,
} from "@prismicio/editor-ui"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { useId, useMemo, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { z } from "zod"

import { HookFormInput } from "../../../app/components/hook-form/HookFormInput"
import { Flag } from "../../../app/components/locales/Flag"
import { LocaleSelection } from "../../../app/components/locales/LocaleSelection"
import {
  addGlossaryRulesToQueryCache,
  cancelGlossaryRulesQueries,
  createGlossaryRules,
  deleteGlossaryRule,
  getGlossaryRulesOptions,
  GlossaryRule,
  type NewGlossaryRule,
  removeGlossaryRuleFromQueryCache,
  updateGlossaryRule,
  type UpdateGlossaryRuleBody,
  updateGlossaryRuleInQueryCache,
} from "../../../app/services/glossary"
import type { Locale } from "../../../app/services/locales"
import {
  trackGlossaryRuleCreated,
  trackGlossaryRuleDeleted,
  trackGlossaryRulesForAllTargetLocalesCreated,
  trackGlossaryRuleUpdated,
} from "../../../app/services/tracking"
import { useRepositoryLocalesQuery } from "../../../settings/locales/useRepositoryLocales"

interface GlossaryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sourceLocaleId: string
  targetLocaleId: string
}

export function GlossaryDialog(props: GlossaryDialogProps) {
  const { open, onOpenChange, sourceLocaleId, targetLocaleId } = props

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size={{ height: "auto", width: 832 }}
      noDescription
    >
      <DialogHeader icon="language" title="Glossary" />
      <DialogContent padding={16} gap={16}>
        <Text>
          <GlossaryDescription /> <AddGlossaryRuleDescription />
        </Text>
        <AddGlossaryRule
          sourceLocaleId={sourceLocaleId}
          targetLocaleId={targetLocaleId}
        />
        <GlossaryRulesTable
          sourceLocaleId={sourceLocaleId}
          targetLocaleId={targetLocaleId}
        />
      </DialogContent>
      <DialogActions>
        <DialogCancelButton color="purple">Close</DialogCancelButton>
      </DialogActions>
    </Dialog>
  )
}

function GlossaryDescription() {
  return (
    <span>
      A glossary is a collection of custom translation rules for specific terms
      and expressions. Each rule defines how a specific source term should be
      translated into the target language. These rules ensure consistent and
      accurate translations when using Prismic's Translate with AI.
    </span>
  )
}

function AddGlossaryRuleDescription() {
  return <span>Add new glossary rule up to 200 rules maximum.</span>
}

interface AddGlossaryRuleProps {
  sourceLocaleId: string
  onSourceLocaleIdChange?: (sourceLocaleId: string) => void
  targetLocaleId: string
  onTargetLocaleIdChange?: (targetLocaleId: string) => void
}

function AddGlossaryRule(props: AddGlossaryRuleProps) {
  const {
    sourceLocaleId,
    onSourceLocaleIdChange,
    targetLocaleId,
    onTargetLocaleIdChange,
  } = props

  const fixedSourceLocaleId = !onSourceLocaleIdChange
  const fixedTargetLocaleId = !onTargetLocaleIdChange

  const { data: locales } = useRepositoryLocalesQuery()

  const sourceLocaleOptions = useMemo(() => {
    if (fixedSourceLocaleId) {
      const sourceLocale = locales.find(
        (locale) => locale.id === sourceLocaleId,
      )
      if (sourceLocale) return [sourceLocale]
    }
    return locales
  }, [locales, fixedSourceLocaleId, sourceLocaleId])

  const targetLocaleOptions = useMemo(() => {
    if (fixedTargetLocaleId) {
      const targetLocale = locales.find(
        (locale) => locale.id === targetLocaleId,
      )
      if (targetLocale) return [targetLocale, allLanguagesOption]
    }
    return [...locales, allLanguagesOption]
  }, [locales, fixedTargetLocaleId, targetLocaleId])

  const glossaryRuleFormSchema = useGlossaryRuleFormSchema({
    sourceLocaleId,
    targetLocaleId,
  })

  const form = useForm<GlossaryRuleForm>({
    resolver: zodResolver(glossaryRuleFormSchema),
    defaultValues: {
      type: "single",
      sourceValue: "",
      targetValue: "",
    },
  })

  const formId = useId()

  const [type] = form.watch(["type"])

  function onSourceLocaleChange(newSourceLocaleId: string) {
    onSourceLocaleIdChange?.(newSourceLocaleId)
    if (newSourceLocaleId === targetLocaleId) {
      const newTargetLocaleId = getDifferentLocaleId(
        targetLocaleOptions,
        newSourceLocaleId,
      )
      onTargetLocaleIdChange?.(newTargetLocaleId)
    }
  }

  function onTargetLocaleChange(newTargetLocaleId: string) {
    if (newTargetLocaleId === allLanguagesOption.id) {
      form.setValue("type", "global")
    } else {
      form.setValue("type", "single")
      onTargetLocaleIdChange?.(newTargetLocaleId)
      if (newTargetLocaleId === sourceLocaleId) {
        const newSourceLocaleId = getDifferentLocaleId(
          sourceLocaleOptions,
          newTargetLocaleId,
        )
        onSourceLocaleIdChange?.(newSourceLocaleId)
      }
    }
  }

  function onFormReset() {
    const values = form.getValues()
    form.reset({
      type: values.type,
      sourceValue: "",
      targetValue: "",
    })
  }

  const { showToast } = useToast()
  const { showAlert } = useAlert()

  const { mutate: addGlossaryRules, isPending } = useMutation({
    mutationFn: async (data: GlossaryRuleForm) => {
      let newGlossaryRules: NewGlossaryRule[]

      if (data.type === "global") {
        newGlossaryRules = createGlossaryRulePairs({
          sourceLocaleId,
          locales,
          sourceValue: data.sourceValue,
          targetValue: data.targetValue,
        })
      } else {
        newGlossaryRules = [
          {
            sourceLocaleId,
            targetLocaleId,
            sourceValue: data.sourceValue,
            targetValue: data.targetValue,
          },
        ]
      }

      const createdGlossaryRules = await createGlossaryRules({
        body: newGlossaryRules,
      })

      return {
        sourceLocaleId,
        createdGlossaryRules,
      }
    },
    onError: (error) => {
      showAlert({
        title: "Failed to add glossary rule",
        subtitle: getHttpErrorMessage(error),
      })
    },
    onSuccess: ({ createdGlossaryRules, sourceLocaleId }, data) => {
      addGlossaryRulesToQueryCache(createdGlossaryRules)

      if (data.type === "global") {
        trackGlossaryRulesForAllTargetLocalesCreated({
          sourceLocale: sourceLocaleId,
          ruleCount: createdGlossaryRules.length,
        })
      }

      createdGlossaryRules.forEach((glossaryRule) => {
        trackGlossaryRuleCreated({
          id: glossaryRule.id,
          sourceLocale: glossaryRule.sourceLocaleId,
          targetLocale: glossaryRule.targetLocaleId,
          sourceValue: glossaryRule.sourceValue,
          targetValue: glossaryRule.targetValue,
          source: data.type === "global" ? "all_target_locales" : "single",
        })
      })

      showToast({
        title: "Glossary rule added successfully",
      })

      onFormReset()
    },
  })

  return (
    <FormProvider {...form}>
      <Form
        id={formId}
        onSubmit={form.handleSubmit((data) => addGlossaryRules(data))}
      >
        <Box flexDirection="column" gap={8}>
          <Text variant="emphasized">Add new glossary rule:</Text>
          <Box gap={8} alignItems="flex-start">
            {sourceLocaleOptions.length > 1 && (
              <LocaleSelection
                locale={{ id: sourceLocaleId, customName: null }}
                onLocaleChange={({ id }) => onSourceLocaleChange(id)}
                locales={sourceLocaleOptions}
                width={272}
              />
            )}
            {sourceLocaleOptions.length === 1 && sourceLocaleOptions[0] && (
              <LocaleReadOnlyButton locale={sourceLocaleOptions[0]} />
            )}
            <HookFormInput
              control={form.control}
              name="sourceValue"
              placeholder="Source text"
              sx={{ flexGrow: 1 }}
            />
          </Box>
          <Box gap={8} alignItems="flex-start">
            <LocaleSelection
              locale={
                type === "global"
                  ? { id: allLanguagesOption.id, customName: null }
                  : { id: targetLocaleId, customName: null }
              }
              onLocaleChange={({ id }) => onTargetLocaleChange(id)}
              locales={targetLocaleOptions}
              width={272}
            />
            <HookFormInput
              control={form.control}
              name="targetValue"
              placeholder="Target text"
              sx={{ flexGrow: 1 }}
            />
          </Box>
          <Box justifyContent="end">
            <Button
              color="grey"
              size="large"
              startIcon="add"
              type="submit"
              form={formId}
              disabled={!form.formState.isDirty}
              loading={isPending}
            >
              Add to glossary
            </Button>
          </Box>
        </Box>
      </Form>
    </FormProvider>
  )
}

interface LocaleReadOnlyButtonProps {
  locale: Locale
}

function LocaleReadOnlyButton(props: LocaleReadOnlyButtonProps) {
  const { locale } = props

  return (
    <Box
      border
      borderRadius={6}
      width={272}
      height={40}
      padding={{ inline: 16 }}
      gap={8}
      alignItems="center"
    >
      <Flag locale={locale.id} />
      <Text noWrap>{locale.label}</Text>
    </Box>
  )
}

const allLanguagesOption = { id: "all", label: "All languages" } as const

interface CreateGlossaryRulePairsArgs {
  sourceLocaleId: string
  locales: Locale[]
  sourceValue: string
  targetValue: string
}

function createGlossaryRulePairs(args: CreateGlossaryRulePairsArgs) {
  const { sourceLocaleId, locales, sourceValue, targetValue } = args

  const pairs: NewGlossaryRule[] = []

  for (const targetLocale of locales) {
    const targetLocaleId = targetLocale.id

    if (targetLocaleId === sourceLocaleId) continue

    pairs.push({
      sourceLocaleId,
      targetLocaleId,
      sourceValue,
      targetValue,
    })
  }

  return pairs
}

interface GlossaryRulesTableProps {
  sourceLocaleId: string
  targetLocaleId: string
}

function GlossaryRulesTable(props: GlossaryRulesTableProps) {
  const { sourceLocaleId, targetLocaleId } = props

  const { data: glossaryRules } = useSuspenseQuery(
    getGlossaryRulesOptions({
      sourceLocaleId,
      targetLocaleId,
    }),
  )

  const { data: locales } = useRepositoryLocalesQuery()

  const sourceLocale = locales.find((locale) => locale.id === sourceLocaleId)

  const targetLocale = locales.find((locale) => locale.id === targetLocaleId)

  return (
    <Table columnLayout="1fr 1fr 40px" isInteractive={false}>
      <TableHead>
        <TableRow>
          <TableCell>
            <Text variant="small" color="grey11" noWrap>
              {sourceLocale?.label}
            </Text>
          </TableCell>
          <TableCell>
            <Text variant="small" color="grey11" noWrap>
              {targetLocale?.label}
            </Text>
          </TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {glossaryRules.length === 0 && (
          <Box flexDirection="column" alignItems="center" padding={24}>
            <Text variant="emphasized" color="grey11">
              This glossary is empty.
            </Text>
            <Text color="grey11">Any rules you add will appear here.</Text>
          </Box>
        )}
        {glossaryRules.map((glossaryRule) => (
          <GlossaryRuleRow
            key={glossaryRule.id}
            glossaryRule={glossaryRule}
            glossaryRules={glossaryRules}
          />
        ))}
      </TableBody>
    </Table>
  )
}

interface GlossaryRuleRowProps {
  glossaryRule: GlossaryRule
  glossaryRules: GlossaryRule[]
}

function GlossaryRuleRow(props: GlossaryRuleRowProps) {
  const { glossaryRule, glossaryRules } = props
  const { id: glossaryRuleId, sourceLocaleId, targetLocaleId } = glossaryRule

  const { showToast } = useToast()
  const { showAlert } = useAlert()

  const deleteGlossaryRuleMutation = useMutation({
    onMutate: () => {
      removeGlossaryRuleFromQueryCache(glossaryRule)
    },
    mutationFn: () => deleteGlossaryRule({ glossaryRuleId }),
    onError: (error) => {
      addGlossaryRulesToQueryCache([glossaryRule])
      showAlert({
        title: `Failed to delete glossary rule ${getGlossaryRuleLabel(glossaryRule)}`,
        subtitle: getHttpErrorMessage(error),
      })
    },
    onSuccess: () => {
      trackGlossaryRuleDeleted({
        id: glossaryRuleId,
        sourceLocale: sourceLocaleId,
        targetLocale: targetLocaleId,
        sourceValue: glossaryRule.sourceValue,
        targetValue: glossaryRule.targetValue,
      })
      showToast({
        title: `Glossary rule ${getGlossaryRuleLabel(glossaryRule)} deleted`,
      })
    },
  })

  const [sourceValue, setSourceValue] = useState(glossaryRule.sourceValue)
  const [targetValue, setTargetValue] = useState(glossaryRule.targetValue)

  const updateGlossaryRuleMutation = useMutation({
    onMutate: async (body) => {
      // Optimistic update
      const oldGlossaryRule = glossaryRule
      updateGlossaryRuleInQueryCache(glossaryRule, body)
      await cancelGlossaryRulesQueries({
        sourceLocaleId,
        targetLocaleId,
      })

      // Rollback function
      function rollback() {
        const update: UpdateGlossaryRuleBody = {}

        if (body.sourceValue) {
          update.sourceValue = oldGlossaryRule.sourceValue
          setSourceValue(oldGlossaryRule.sourceValue)
        }

        if (body.targetValue) {
          update.targetValue = oldGlossaryRule.targetValue
          setTargetValue(oldGlossaryRule.targetValue)
        }

        updateGlossaryRuleInQueryCache(oldGlossaryRule, update)
      }

      return { rollback, oldGlossaryRule }
    },
    mutationFn: (body: UpdateGlossaryRuleBody) =>
      updateGlossaryRule({
        glossaryRuleId,
        body,
      }),
    onError: (error, _body, context) => {
      context?.rollback()
      showAlert({
        title: `Failed to update glossary rule ${getGlossaryRuleLabel(glossaryRule)}`,
        subtitle: getHttpErrorMessage(error),
      })
    },
    onSuccess: (newGlossaryRule, _body, { oldGlossaryRule }) => {
      trackGlossaryRuleUpdated({
        id: glossaryRuleId,
        sourceLocale: sourceLocaleId,
        targetLocale: targetLocaleId,
        sourceValueOld: oldGlossaryRule.sourceValue,
        sourceValueNew: newGlossaryRule.sourceValue,
        targetValueOld: oldGlossaryRule.targetValue,
        targetValueNew: newGlossaryRule.targetValue,
      })
      showToast({
        title: `Glossary rule ${getGlossaryRuleLabel(newGlossaryRule)} updated`,
      })
    },
  })

  const updateGlossaryRuleSchema = useUpdateGlossaryRuleSchema({
    glossaryRule,
    glossaryRules,
  })
  const sourceValueSchema = updateGlossaryRuleSchema.shape.sourceValue
  const targetValueSchema = updateGlossaryRuleSchema.shape.targetValue

  const sourceValueError =
    sourceValueSchema.safeParse(sourceValue).error?.issues[0]?.message

  const targetValueError =
    targetValueSchema.safeParse(targetValue).error?.issues[0]?.message

  function onSourceValueEditComplete() {
    const newSourceValue = sourceValue.trim()
    if (newSourceValue === glossaryRule.sourceValue) return
    if (sourceValueError) {
      setSourceValue(glossaryRule.sourceValue)
      return
    }
    updateGlossaryRuleMutation.mutate({ sourceValue: newSourceValue })
  }

  function onTargetValueEditComplete() {
    const newTargetValue = targetValue.trim()
    if (newTargetValue === glossaryRule.targetValue) return
    if (targetValueError) {
      setTargetValue(glossaryRule.targetValue)
      return
    }
    updateGlossaryRuleMutation.mutate({ targetValue: newTargetValue })
  }

  return (
    <TableRow>
      <TableCell alignItems="flex-start">
        <Box flexDirection="column" width="100%">
          <EditableText
            name="sourceValue"
            aria-label="Edit source value"
            color="grey12"
            value={sourceValue}
            onBlur={onSourceValueEditComplete}
            onChange={setSourceValue}
            sx={{ width: "100%", marginTop: 2 }}
          />
          <AnimatedFormError error={sourceValueError} />
        </Box>
      </TableCell>
      <TableCell alignItems="flex-start">
        <Box flexDirection="column" width="100%">
          <EditableText
            name="targetValue"
            aria-label="Edit target value"
            color="grey12"
            value={targetValue}
            onBlur={onTargetValueEditComplete}
            onChange={setTargetValue}
            sx={{ width: "100%", marginTop: 2 }}
          />
          <AnimatedFormError error={targetValueError} />
        </Box>
      </TableCell>
      <TableCell alignItems="flex-start">
        <Button
          startIcon="delete"
          color="grey"
          onClick={() => deleteGlossaryRuleMutation.mutate()}
        />
      </TableCell>
    </TableRow>
  )
}

const glossaryRuleValueSchema = z
  .string()
  .trim()
  .min(1, "Required")
  .max(100, "Must be equal to or less than 100 characters long")

interface UseGlossaryRuleFormSchemaArgs {
  sourceLocaleId: string
  targetLocaleId: string
}

function useGlossaryRuleFormSchema(args: UseGlossaryRuleFormSchemaArgs) {
  const { sourceLocaleId, targetLocaleId } = args

  const { data: glossaryRules } = useSuspenseQuery(
    getGlossaryRulesOptions({
      sourceLocaleId,
      targetLocaleId,
    }),
  )

  return useMemo(() => {
    const sourceValueSchema = glossaryRuleValueSchema.refine(
      (value) => {
        // Disallow duplicate rules
        return !glossaryRules.some(
          (rule) =>
            rule.sourceValue === value &&
            rule.sourceLocaleId === sourceLocaleId &&
            rule.targetLocaleId === targetLocaleId,
        )
      },
      {
        message: "Glossary rule already exists",
      },
    )

    const addGlossaryRuleSchema = z.object({
      type: z.literal("single"),
      sourceValue: sourceValueSchema,
      targetValue: glossaryRuleValueSchema,
    })

    /* NOTE: For global rules, duplicate validation is only handled against
     * current locale pair, so we don't need to load glossary rules for all
     * target locales. That's why adding global rule can still fail on duplicate
     * source value validation on server.
     */
    const addGlobalGlossaryRuleSchema = z.object({
      type: z.literal("global"),
      sourceValue: sourceValueSchema,
      targetValue: glossaryRuleValueSchema,
    })

    const addGlossaryRuleFormSchema = z.discriminatedUnion("type", [
      addGlossaryRuleSchema,
      addGlobalGlossaryRuleSchema,
    ])

    return addGlossaryRuleFormSchema
  }, [sourceLocaleId, targetLocaleId, glossaryRules])
}

export type GlossaryRuleForm = z.infer<
  ReturnType<typeof useGlossaryRuleFormSchema>
>

interface UseUpdateGlossaryRuleSchemaArgs {
  glossaryRule: GlossaryRule
  glossaryRules: GlossaryRule[]
}

function useUpdateGlossaryRuleSchema(args: UseUpdateGlossaryRuleSchemaArgs) {
  const { glossaryRule, glossaryRules } = args

  return useMemo(() => {
    const sourceValueSchema = glossaryRuleValueSchema.refine(
      (value) => {
        // Allow editing the same rule
        if (glossaryRule.sourceValue === value) return true

        // Disallow duplicate rules
        return !glossaryRules.some(
          (rule) =>
            rule.sourceValue === value &&
            rule.sourceLocaleId === glossaryRule.sourceLocaleId &&
            rule.targetLocaleId === glossaryRule.targetLocaleId,
        )
      },
      {
        message: "Glossary rule already exists",
      },
    )

    return z.object({
      sourceValue: sourceValueSchema,
      targetValue: glossaryRuleValueSchema,
    })
  }, [glossaryRules, glossaryRule])
}

function getDifferentLocaleId(locales: Locale[], localeId: string) {
  return locales.find((locale) => locale.id !== localeId)?.id ?? ""
}

function getGlossaryRuleLabel(glossaryRule: GlossaryRule) {
  return `"${glossaryRule.sourceValue}"→"${glossaryRule.targetValue}"`
}

function getHttpErrorMessage(error: unknown) {
  if (
    isRequestError(error) &&
    error.detail.type === "http" &&
    isPrismicHttpErrorResponse(error.detail.body)
  ) {
    return error.detail.body.message
  }
  return undefined
}
